<template>
  <ConfirmModal
    :approve="t('FestivalShop.orders.manage.delivery_time_change_modal_accept')"
    :disabled="isLoading"
    wrapper-class="w-full max-w-2xl rounded-lg"
    persist
    @close-dialog="handleModal"
  >
    <template #title>
      <p class="mb-0 p-6 pr-12 txt-body-xl-bold">{{ title }}</p>
    </template>
    <template #content>
      <div class="p-6">
        <SelectField id="deliveryTimeId" v-model="deliveryTimeId" name="deliveryTime">
          <option v-if="showRequiredDeliveryTimeslotPlaceholder" :value="''" disabled>
            {{ requiredDeliveryTimeslotPlaceholder }}
          </option>
          <option v-if="!deliveryTimeslotSelectionRequired" :value="''">
            {{ $t(defaultDeliveryPhraseKey) }}
          </option>
          <option
            v-for="slot in deliverySlotOptions"
            :key="slot.id"
            :value="slot.id"
            :disabled="selectedSlotUnavailable && slot.id === category.kioskSelectedDeliverySlot?.id"
          >
            {{ formatDeliverySlot(slot, locale as LocaleEnum)
            }}{{
              selectedSlotUnavailable && slot.id === category.kioskSelectedDeliverySlot?.id
                ? ` - ${t('FestivalShop.products.details.configure.delivery_time.selected_unavailable')}`
                : ''
            }}
          </option>
        </SelectField>
      </div>
    </template>
  </ConfirmModal>
</template>

<script lang="ts" setup>
import { SelectField } from '@ramp106/omrjs-core-ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ConfirmModal from '@/components/ConfirmModal.vue'
import {
  LocaleEnum,
  useDeleteKioskCategoryDeliverySlotMutation,
  useSetKioskCategoryDeliverySlotMutation,
  type ProductCategoryDeliverySlot,
  type ProductCategory,
} from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'

import { formatDeliverySlot } from '..'

interface Props {
  category: ProductCategory
  kioskId: string
}

const props = defineProps<Props>()
const emits = defineEmits<{
  close: []
}>()

const deliveryTimeId = ref<string>(props.category.kioskSelectedDeliverySlot?.id ?? '')

const { locale, t } = useI18n()
const { mutate: setDeliverySlot, loading: setDeliverySlotLoading } = useSetKioskCategoryDeliverySlotMutation()
const { mutate: deleteDeliverySlot, loading: deleteDeliverySlotLoading } = useDeleteKioskCategoryDeliverySlotMutation()

const title = computed(() => {
  return `${t('FestivalShop.orders.manage.delivery_time_change_modal_title')} ${props.category.name}`
})

const isLoading = computed(() => setDeliverySlotLoading.value || deleteDeliverySlotLoading.value)
const deliveryTimeslotSelectionRequired = computed(
  () => props.category.enableDeliveryTimeslots && props.category.deliveryTimeslotSelectionRequired,
)
const showRequiredDeliveryTimeslotPlaceholder = computed(() => {
  if (!deliveryTimeslotSelectionRequired.value) return false
  if (props.category.kioskSelectedDeliverySlot?.id) return false
  return !deliveryTimeId.value
})
const requiredDeliveryTimeslotPlaceholder = computed(() =>
  locale.value === 'de' ? 'Bitte wähle eine Lieferzeit' : 'Please select a delivery time',
)
const selectedSlotUnavailable = computed(() => {
  if (!props.category.kioskSelectedDeliverySlot) return false
  return !props.category.deliverySlots.some((slot) => slot.id === props.category.kioskSelectedDeliverySlot?.id)
})
const defaultDeliveryPhraseKey = computed(() =>
  props.category.secondDayDeliveryDefault
    ? 'FestivalShop.products.details.configure.delivery_time.second_day_overnight_key'
    : 'FestivalShop.products.details.configure.delivery_time.overnight_key',
)
const deliverySlotOptions = computed<ProductCategoryDeliverySlot[]>(() => {
  const selectedSlot = props.category.kioskSelectedDeliverySlot
  if (!selectedSlotUnavailable.value || !selectedSlot) return props.category.deliverySlots
  return [selectedSlot, ...props.category.deliverySlots]
})

const clearDeliverySelection = async () => {
  try {
    const result = await deleteDeliverySlot(
      { kioskId: props.kioskId, productCategoryId: props.category.id },
      {
        refetchQueries: ['FestivalShopProductCategories', 'FestivalShopProducts'],
      },
    )

    const errors = result?.data?.deleteKioskCategoryDeliverySlot?.errors ?? []
    if (errors.length) {
      showNotification(errors.join(', '), 'error')
      return false
    }

    deliveryTimeId.value = ''
    return true
  } catch {
    showNotification(t('FestivalShop.products.details.notification.delivery_time_change_error'), 'error')
    return false
  }
}

const updateDeliverySelection = async (deliveryId: string) => {
  if (!deliveryId.length) {
    if (deliveryTimeslotSelectionRequired.value) {
      showNotification(t('FestivalShop.products.details.disabled_not_configured'), 'error')
      return
    }
    const isCleared = await clearDeliverySelection()
    if (isCleared) {
      showNotification(t('FestivalShop.products.details.notification.delivery_time_changed'), 'success')
      emits('close')
    }
    return
  }

  try {
    const result = await setDeliverySlot(
      {
        kioskId: props.kioskId,
        productCategoryDeliverySlotId: deliveryId,
        productCategoryId: props.category.id,
      },
      {
        refetchQueries: ['FestivalShopProductCategories', 'FestivalShopProducts'],
      },
    )

    const errors = result?.data?.createKioskCategoryDeliverySlot?.errors ?? []
    if (errors.length) {
      const isCleared = await clearDeliverySelection()
      if (isCleared) {
        showNotification(t('FestivalShop.products.details.notification.delivery_time_unavailable_reset'), 'warning')
        emits('close')
      }
      return
    }

    showNotification(t('FestivalShop.products.details.notification.delivery_time_changed'), 'success')
    emits('close')
  } catch {
    showNotification(t('FestivalShop.products.details.notification.delivery_time_change_error'), 'error')
  }
}

const handleModal = async (approved: boolean) => {
  if (!approved) {
    emits('close')
    return
  }

  await updateDeliverySelection(deliveryTimeId.value)
}
</script>
